import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Button, Divider, Tooltip, Paper, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup, FormControlLabel, Radio, Grid, TextField, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { CheckCircle, HourglassEmpty, Send as SendIcon, AutoFixHigh as GenerateIcon, MoreHoriz as MoreHorizIcon, ReportProblem as ReportProblemIcon, Assignment as AssignIcon, CheckCircleOutline, HourglassTop } from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs, limit, doc, updateDoc, serverTimestamp, orderBy } from 'firebase/firestore';
import { functions, db } from '../../firebase';
import { toast } from 'react-toastify';
import IcrReviewModal from './IcrReviewModal';
const triggerFirstSavvaiSignal = httpsCallable(functions, 'triggerFirstSavvaiSignal', { timeout: 540000 }); // 9-minute timeout

const sendOnboardingReminderEmail = httpsCallable(functions, 'sendOnboardingReminderEmail');
const generateInitialCompanyResearch = httpsCallable(functions, 'generateInitialCompanyResearch', { timeout: 300000 }); // 300,000ms = 5 minutes

// --- Assign Template Modal Component ---
function AssignTemplateModal({ open, onClose, onAssign, user }) {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTemplateId, setSelectedTemplateId] = useState(user?.assignedQuestionnaireTemplateId || '');
    const [userSubmittedInfo, setUserSubmittedInfo] = useState(null);


    useEffect(() => {
        if (open) {
            const fetchTemplates = async () => {
                setLoading(true);
                try {
                    const q = query(collection(db, 'questionnaireTemplates'), where("status", "==", "Active"), orderBy("name"));
                    const querySnapshot = await getDocs(q);
                    const activeTemplates = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    setTemplates(activeTemplates);
                } catch (error) {
                    console.error("Error fetching active templates:", error);
                    toast.error("Failed to fetch questionnaire templates.");
                } finally {
                    setLoading(false);
                }
            };
            fetchTemplates();
        }
    }, [open]);

    const handleAssign = () => {
        const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
        if (selectedTemplate) {
            onAssign(selectedTemplate);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Assign Questionnaire Template</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}><CircularProgress /></Box>
                ) : templates.length > 0 ? (
                    <RadioGroup
                        value={selectedTemplateId}
                        onChange={(e) => setSelectedTemplateId(e.target.value)}
                    >
                        {templates.map(template => (
                            <FormControlLabel 
                                key={template.id} 
                                value={template.id} 
                                control={<Radio />} 
                                label={`${template.name} (v${template.version})`} 
                            />
                        ))}
                    </RadioGroup>
                ) : (
                    <Typography>No active templates found.</Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleAssign} disabled={!selectedTemplateId || loading}>Save Assignment</Button>
            </DialogActions>
        </Dialog>
    );
}

// --- Checklist Item Helper Component ---
const ChecklistItem = ({ icon, primary, secondary, action }) => (
    <ListItem sx={{ py: 2 }}>
        <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>
        <ListItemText primary={<Typography variant="subtitle1" sx={{ fontWeight: 500 }}>{primary}</Typography>} secondary={secondary} />
        {action}
    </ListItem>
);

// --- Main Component ---
export default function ClientSetupChecklist({ client, user, onClientUpdate }) {
    console.log('[DEBUG] ClientSetupChecklist received props:', { 
        clientId: client?.id, 
        clientName: client?.name, 
        hasOnClientUpdate: !!onClientUpdate 
    });
    const [areFinancialsComplete, setAreFinancialsComplete] = useState(false);
    const [financialsAwaitingProcessing, setFinancialsAwaitingProcessing] = useState(false); // Add this line
    const [loadingFinancials, setLoadingFinancials] = useState(true);
    const [isQuestionnaireComplete, setIsQuestionnaireComplete] = useState(false);
    const [isQuestionnaireProcessed, setIsQuestionnaireProcessed] = useState(false); // Add this line
    const [loadingQuestionnaire, setLoadingQuestionnaire] = useState(true);
    const [isAssignTemplateModalOpen, setIsAssignTemplateModalOpen] = useState(false);
    const [isReadyForActivation, setIsReadyForActivation] = useState(false);

    // State for ICR
    const [isIcrReviewModalOpen, setIsIcrReviewModalOpen] = useState(false);
    const [icrReportContent, setIcrReportContent] = useState('');
    const [identifiedIndustry, setIdentifiedIndustry] = useState('');
    const [isGeneratingIcr, setIsGeneratingIcr] = useState(false);
    const [userSubmittedInfo, setUserSubmittedInfo] = useState(null);
    const isTriggering = useRef(false);

    // Add these new state variables for Company Identification
const [companyInfo, setCompanyInfo] = useState({
    officialCompanyName: '',
    companyWebsite: '',
    city: '',
    state: '',
    country: ''
});
const [isEditingCompanyInfo, setIsEditingCompanyInfo] = useState(false);
const [isSavingCompanyInfo, setIsSavingCompanyInfo] = useState(false);
const [isActivating, setIsActivating] = useState(false);
const finalizeAndActivateClient = httpsCallable(functions, 'finalizeAndActivateClient', { timeout: 540000 }); // 540000ms = 9 minutes
// Add this useEffect to load existing data for the client
useEffect(() => {
    if (client) {
        // Start with the client's currently saved official data
        const info = {
            officialCompanyName: client.officialCompanyName || '',
            companyWebsite: client.companyWebsite || '',
            city: client.city || '',
            state: client.state || '',
            country: client.country || ''
        };

        // Check for user-submitted data
        if (client.submittedForAdminReview) {
            setUserSubmittedInfo(client.submittedForAdminReview);
            // If the official info is blank, pre-fill it with the user's submission
            if (!info.officialCompanyName) {
                info.officialCompanyName = client.submittedForAdminReview.companyName || '';
            }
            if (!info.companyWebsite) {
                info.companyWebsite = client.submittedForAdminReview.websiteUrl || '';
            }
        } else {
            setUserSubmittedInfo(null);
        }

        setCompanyInfo(info);

        // If the step is not yet complete, default to edit mode.
        if (client.companyInfoCompleted === false) {
            setIsEditingCompanyInfo(true);
        } else {
            setIsEditingCompanyInfo(false);
        }
    }
}, [client]);

    // Memoized derived state to prevent re-calculations on every render
    const hasQuestionnaireTemplate = !!user?.assignedQuestionnaireTemplateId;
    const isIcrComplete = !!client?.companyResearchReport;

    const fetchChecklistStatuses = useCallback(async () => {
        if (!client?.id || !user?.id) return;

        // Reset all states at the beginning of the fetch
        setLoadingFinancials(true);
        setAreFinancialsComplete(false);
        setFinancialsAwaitingProcessing(false);
        setLoadingQuestionnaire(true);
        setIsQuestionnaireComplete(false);
        setIsQuestionnaireProcessed(false);

        try {
            // --- Define Financials Queries ---
            const processedFinancialsQuery = query(
                collection(db, 'documents'),
                where("clientId", "==", client.id),
                where("type", "==", "financial"),
                where("documentStatus", "==", "Processed"),
                limit(1)
            );
            const awaitingFinancialsQuery = query(
                collection(db, 'documents'),
                where("clientId", "==", client.id),
                where("type", "==", "financial"),
                where("documentStatus", "in", ["Awaiting Sanitization", "Sanitized"]),
                limit(1)
            );

            // --- Define Questionnaire Query ---
            let questionnaireQuery = null;
            if (user.assignedQuestionnaireTemplateId) {
                questionnaireQuery = query(
                    collection(db, `clients/${client.id}/ceoQuestionnaireResponses`),
                    where("questionnaireTemplateId", "==", user.assignedQuestionnaireTemplateId),
                    where("status", "in", ["submitted_pii_review_pending", "pii_review_complete", "synthesis_complete", "Sanitized"]),
                    orderBy("submittedAt", "desc"),
                    limit(1)
                );
            }

            // --- Run all queries concurrently for efficiency ---
            const [
                processedFinancialsSnapshot,
                awaitingFinancialsSnapshot,
                questionnaireSnapshot
            ] = await Promise.all([
                getDocs(processedFinancialsQuery),
                getDocs(awaitingFinancialsQuery),
                questionnaireQuery ? getDocs(questionnaireQuery) : Promise.resolve(null)
            ]);

            // --- Set State based on query results ---
// Financials — check awaiting first: new uploads needing processing always take priority
const processedDocsExist = !processedFinancialsSnapshot.empty;
const qboFlagIsSet = !!client?.financialsUploaded;
const awaitingDocsExist = !awaitingFinancialsSnapshot.empty;

if (awaitingDocsExist) {
    // New doc waiting for admin processing — always show this state,
    // even if older processed docs or QB flag exist
    setFinancialsAwaitingProcessing(true);
} else if (processedDocsExist || qboFlagIsSet) {
    setAreFinancialsComplete(true);
}

            // Questionnaire
            if (questionnaireSnapshot && !questionnaireSnapshot.empty) {
                setIsQuestionnaireComplete(true);
                const submittedDocData = questionnaireSnapshot.docs[0].data();
                if (submittedDocData.aiSynthesizedNarrative) {
                    setIsQuestionnaireProcessed(true);
                }
            }

        } catch (error) {
            console.error("[DEBUG] Error fetching checklist statuses:", error);
            // Reset all on error to be safe
            setAreFinancialsComplete(false);
            setFinancialsAwaitingProcessing(false);
            setIsQuestionnaireComplete(false);
            setIsQuestionnaireProcessed(false);
        } finally {
            setLoadingFinancials(false);
            setLoadingQuestionnaire(false);
        }
    }, [client?.id, user?.id, user?.assignedQuestionnaireTemplateId]);

    useEffect(() => {
        fetchChecklistStatuses();
    }, [fetchChecklistStatuses]);

    // This effect runs whenever a checklist item's status changes.
// In ClientSetupChecklist.js
useEffect(() => {
  // Check if all prerequisite tasks are complete
  const companyInfoComplete = !!client?.officialCompanyName && !!client?.companyWebsite;
  const isIcrComplete = !!client?.companyResearchReport;
  const financialsReady = areFinancialsComplete || !!client?.financialsSkipped;
  const allPrerequisitesMet = companyInfoComplete && isIcrComplete && isQuestionnaireProcessed && financialsReady;

  // Set state to control the visibility of the final activation button
  setIsReadyForActivation(allPrerequisitesMet);

}, [client, isIcrComplete, isQuestionnaireProcessed, areFinancialsComplete]);

    const handleSaveCompanyInfo = async () => {
    if (!client?.id) {
        toast.error("Client ID is missing. Cannot save.");
        return;
    }
    setIsSavingCompanyInfo(true);
    const toastId = toast.loading("Saving company information...");
    try {
        const updateClientDetails = httpsCallable(functions, 'updateClientDetails');

        // Prepare data payload for the backend function
        const dataToSave = {
            ...companyInfo, // The state from our form
            name: companyInfo.officialCompanyName, // Add the 'name' field for the backend validation
            companyInfoCompleted: true // This is the flag that marks the step as complete
        };
        
        await updateClientDetails({
            clientId: client.id,
            updatedData: dataToSave
        });
        
        toast.update(toastId, { render: "Company information saved successfully!", type: "success", isLoading: false, autoClose: 4000 });
        setIsEditingCompanyInfo(false);
        if (onClientUpdate) {
            onClientUpdate();
        }
    } catch (error) {
        console.error("Error saving company info:", error);
        toast.update(toastId, { render: `Failed to save: ${error.message}`, type: "error", isLoading: false, autoClose: 6000 });
    } finally {
        setIsSavingCompanyInfo(false);
    }
};

    const handleAssignTemplate = async (template) => {
    const toastId = toast.loading("Assigning template...");
    try {
        // Use the secure, existing adminUpdateUser Cloud Function
        const adminUpdateUser = httpsCallable(functions, 'adminUpdateUser');
        await adminUpdateUser({
            userId: user.id,
            assignedQuestionnaireTemplateId: template.id,
            assignedQuestionnaireTemplateVersion: template.version
        });

        toast.update(toastId, { render: "Template assigned successfully!", type: "success", isLoading: false, autoClose: 4000 });
        setIsAssignTemplateModalOpen(false);
        onClientUpdate(); // This is crucial to refresh the parent component's user/client data
    } catch (error) {
        console.error("Error assigning template:", error);
        toast.update(toastId, { render: `Failed to assign template: ${error.message}`, type: "error", isLoading: false, autoClose: 6000 });
    }
};
    
    const handleSendReminder = async (reminderType) => {
        const toastId = toast.loading(`Sending ${reminderType} reminder...`);
        try {
            await sendOnboardingReminderEmail({ userId: user.id, reminderType: reminderType, clientName: client.name });
            toast.update(toastId, { render: "Reminder email sent successfully!", type: "success", isLoading: false, autoClose: 4000 });
        } catch (error) {
            console.error(`Error sending ${reminderType} reminder:`, error);
            toast.update(toastId, { render: `Failed to send reminder: ${error.message}`, type: "error", isLoading: false, autoClose: 5000 });
        }
    };

    const handleGenerateAndReviewIcr = async () => {
    // First, check that the required info has been saved.
    if (!client?.officialCompanyName || !client?.companyWebsite) {
        toast.error("Please save the Official Company Name and Website before generating the ICR.");
        return;
    }

    setIsGeneratingIcr(true);
    const toastId = toast.loading("Generating AI research... This may take up to a minute.");
    try {
        // We now only need to send the client's ID.
        // The backend will be responsible for looking up the name and website.
        const result = await generateInitialCompanyResearch({
  clientId: client.id
});

// --- FIX START: Ensure we correctly access the report data ---
// The backend returns { report: "...", industry: "..." } inside result.data
const reportText = result.data.report;
const industry = result.data.industry || 'General';

if (typeof reportText !== 'string') {
    console.error("Received unexpected data structure for ICR report:", result.data);
    toast.error("Failed to parse the generated report. Check console for details.");
    setIcrReportContent("Error: The AI generated a report in an unexpected format.");
} else {
    setIcrReportContent(reportText);
}

setIdentifiedIndustry(industry);
setIsIcrReviewModalOpen(true);
// --- FIX END ---
        toast.update(toastId, { render: "Report generated! Please review.", type: "success", isLoading: false, autoClose: 5000 });
    } catch (error) {
        console.error("Error generating ICR:", error);
        toast.update(toastId, { render: `Failed to generate report: ${error.message}`, type: "error", isLoading: false, autoClose: 6000 });
    } finally {
        setIsGeneratingIcr(false);
    }
};

    const handleSaveIcr = async (reportText) => {
        const toastId = toast.loading("Saving report...");
        try {
            const clientRef = doc(db, 'clients', client.id);
            await updateDoc(clientRef, {
                companyResearchReport: reportText,
                industry: identifiedIndustry,
                companyResearchReportLastUpdated: serverTimestamp(),
                companyResearchReportLastUpdatedBy: user.email,
                icrLastGeneratedAt: serverTimestamp()
            });
            toast.update(toastId, { render: "Report saved successfully!", type: "success", isLoading: false, autoClose: 4000 });
            setIsIcrReviewModalOpen(false);
            onClientUpdate();
        } catch(error) {
            console.error("Error saving ICR:", error);
            toast.update(toastId, { render: `Failed to save report: ${error.message}`, type: "error", isLoading: false, autoClose: 6000 });
        }
    };
    // ADD THIS NEW FUNCTION:
const handleFinalizeAndActivate = () => {
  console.log('[DIAGNOSTIC] "Finalize" button clicked.');
  if (!client?.id) {
    toast.error("Cannot activate: Client ID is missing.");
    console.error("[DIAGNOSTIC] Aborting: Client ID is missing.");
    return;
  }

  setIsActivating(true);
  const toastId = toast.loading("Calling backend function...");
  console.log(`[DIAGNOSTIC] Attempting to call 'finalizeAndActivateClient' with clientId: ${client.id}`);
  
  // Using .then().catch() for more detailed error reporting in this specific case
  finalizeAndActivateClient({ clientId: client.id })
    .then((result) => {
      console.log("[DIAGNOSTIC] Backend function returned SUCCESS.", result);
      toast.update(toastId, {
        render: result.data.message || "Client activated successfully!",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });
      if (onClientUpdate) {
        onClientUpdate();
      }
    })
    .catch((error) => {
      // This is the part we are hoping to see.
      console.error('[DIAGNOSTIC] The call to the backend function FAILED. Full error object:', error);
      toast.update(toastId, {
        render: `Activation failed: ${error.message}`,
        type: "error",
        isLoading: false,
        autoClose: 6000,
      });
    })
    .finally(() => {
      setIsActivating(false);
    });
};
    // GUARD CLAUSE: Prevents crash on initial render
    if (!client || !user) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
    <Box>
        <Typography variant="h5" gutterBottom>Client Onboarding Checklist</Typography>
        
        {/* Step 1: Company Identification */}
<Paper sx={{ p: 2, mb: 2, mx: 2, mt: 2 }} elevation={2}>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ mr: 2 }}>
            {client?.companyInfoCompleted ? (
                <CheckCircleOutline sx={{ color: 'success.main', fontSize: 32 }} />
            ) : (
                <HourglassTop sx={{ color: 'warning.main', fontSize: 32 }} />
            )}
        </Box>
        <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div">
                Company Identification
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {client?.companyInfoCompleted
                    ? `Identified as: ${client.officialCompanyName}`
                    : "Official company name and website are required for AI processing."}
            </Typography>
        </Box>
        {/* Show Edit button only if the step is complete and not already being edited */}
        {client?.companyInfoCompleted && !isEditingCompanyInfo && (
            <Button variant="outlined" onClick={() => setIsEditingCompanyInfo(true)}>
                Edit Info
            </Button>
        )}
    </Box>

    {/* If the step is NOT complete, or if admin is editing, show the form */}
    {(!client?.companyInfoCompleted || isEditingCompanyInfo) && (
        <Box sx={{ mt: 2, pl: { xs: 0, sm: '50px' } /* Aligns with text on larger screens */ }}>
            {/* Display user's submission if it exists, as a guide for the admin */}
{userSubmittedInfo && (
    <Box>
        <Divider sx={{ pt: 2, mb: 2 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
    User-Submitted Information for Review:
</Typography>
    </Box>
)}

            {/* The Admin's Edit/Confirmation Form */}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField fullWidth required label="Official Company Name" value={companyInfo.officialCompanyName} onChange={(e) => setCompanyInfo({ ...companyInfo, officialCompanyName: e.target.value })} variant="outlined" size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField fullWidth required label="Company Website" value={companyInfo.companyWebsite} onChange={(e) => setCompanyInfo({ ...companyInfo, companyWebsite: e.target.value })} variant="outlined" size="small" />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="City" value={companyInfo.city} onChange={(e) => setCompanyInfo({ ...companyInfo, city: e.target.value })} variant="outlined" size="small" />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="State / Province" value={companyInfo.state} onChange={(e) => setCompanyInfo({ ...companyInfo, state: e.target.value })} variant="outlined" size="small" />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Country" value={companyInfo.country} onChange={(e) => setCompanyInfo({ ...companyInfo, country: e.target.value })} variant="outlined" size="small" />
                </Grid>
            </Grid>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={() => setIsEditingCompanyInfo(false)} disabled={isSavingCompanyInfo}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSaveCompanyInfo} disabled={!companyInfo.officialCompanyName || !companyInfo.companyWebsite || isSavingCompanyInfo} sx={{ ml: 2 }}>
                    {isSavingCompanyInfo ? <CircularProgress size={24} /> : "Save"}
                </Button>
            </Box>
        </Box>
    )}
</Paper>
        
        <Paper elevation={2} sx={{ mx: 2, mb: 2 }}>
            <List sx={{ p: 1 }}>
                <Divider component="li" variant="inset" sx={{ ml: '72px' }} />
                {/* CEO Questionnaire */}
                <ChecklistItem
                    icon={
                        loadingQuestionnaire ? <MoreHorizIcon color="disabled" /> :
                            isQuestionnaireProcessed ? <CheckCircle color="success" /> :
                                isQuestionnaireComplete ? <HourglassEmpty color="warning" /> :
                                    !hasQuestionnaireTemplate ? <ReportProblemIcon color="error" /> :
                                        <HourglassEmpty color="warning" />
                    }
                    primary="CEO Questionnaire"
                    secondary={
                        loadingQuestionnaire ? "Checking status..." :
                            isQuestionnaireProcessed ? "Questionnaire has been processed and synthesized." :
                                isQuestionnaireComplete ? "Completed and submitted by client." :
                                    !hasQuestionnaireTemplate ? "No template assigned to this user." :
                                        "Awaiting submission from client."
                    }
                    action={
                        !loadingQuestionnaire && (
                            <>
                                {!hasQuestionnaireTemplate && (
                                    <Button variant="contained" size="small" startIcon={<AssignIcon />} onClick={() => setIsAssignTemplateModalOpen(true)}>
                                        Assign Template
                                    </Button>
                                )}
                                {hasQuestionnaireTemplate && !isQuestionnaireComplete && (
                                    <Button variant="outlined" size="small" startIcon={<SendIcon />} onClick={() => handleSendReminder('questionnaire')}>
                                        Send Reminder
                                    </Button>
                                )}
                            </>
                        )
                    }
                />
                <Divider component="li" variant="inset" sx={{ ml: '72px' }} />
                {/* Financials */}
                <ChecklistItem
                    icon={
                        loadingFinancials ? <MoreHorizIcon color="disabled" /> :
                            areFinancialsComplete ? <CheckCircle color="success" /> :
                                client?.financialsSkipped ? <CheckCircle color="success" /> :
                                    <HourglassEmpty color="warning" />
                    }
                    primary={`Financial Documents${client?.financialsSkipped && !areFinancialsComplete ? ' (Skipped by User)' : ''}`}
                    secondary={
                        loadingFinancials ? "Checking status..." :
                            areFinancialsComplete ? "A financial document has been processed." :
                                client?.financialsSkipped ? "User skipped financials upload — no action required. Edge uses questionnaire data for financial calibration. User can add full financials later from Manage Clients." :
                                    financialsAwaitingProcessing ? "Document is ready for admin processing." :
                                        "Awaiting document upload."
                    }
                    action={
                        !loadingFinancials && !areFinancialsComplete && !financialsAwaitingProcessing && !client?.financialsSkipped && (
                            <Button variant="outlined" size="small" startIcon={<SendIcon />} onClick={() => handleSendReminder('financials')}>
                                Send Reminder
                            </Button>
                        )
                    }
                />
                <Divider component="li" variant="inset" sx={{ ml: '72px' }} />
                {/* Initial Company Research */}
                <ChecklistItem
                    icon={isIcrComplete ? <CheckCircle color="success" /> : <HourglassEmpty color="warning" />}
                    primary="Initial Company Research (ICR)"
                    secondary={isIcrComplete ? "Generated and saved to client profile." : "Ready for AI-powered generation."}
                    action={
                        (
                            <Tooltip title="Generate AI-drafted company research report">
                                <span>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={isGeneratingIcr ? <CircularProgress size={16} color="inherit" /> : <GenerateIcon />}
                                        onClick={handleGenerateAndReviewIcr}
                                        disabled={isGeneratingIcr}
                                    >
                                        Generate ICR
                                    </Button>
                                </span>
                            </Tooltip>
                        )
                    }
                />
            </List>
        </Paper>
          {/* ADD THIS NEW BLOCK FOR THE ACTIVATION BUTTON */}
  {isReadyForActivation && (
    <Paper elevation={2} sx={{ mx: 2, mb: 2, p: 2, backgroundColor: 'primary.light' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" sx={{ color: 'primary.contrastText' }}>Ready for Activation</Typography>
          <Typography variant="body2" sx={{ color: 'primary.contrastText' }}>
            All setup steps have been completed by the admin.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircle />}
          onClick={handleFinalizeAndActivate}
          disabled={isActivating}
        >
          Finalize & Activate Client
        </Button>
      </Box>
    </Paper>
  )}
  {/* END OF NEW BLOCK */}

        <AssignTemplateModal 
            open={isAssignTemplateModalOpen}
            onClose={() => setIsAssignTemplateModalOpen(false)}
            onAssign={handleAssignTemplate}
            user={user}
        />
        <IcrReviewModal
            open={isIcrReviewModalOpen}
            onClose={() => setIsIcrReviewModalOpen(false)}
            onSave={handleSaveIcr}
            reportContent={icrReportContent}
            clientName={client.name}
        />
    </Box>
);
}